import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './events-merac.css'

// Events tab — one card per MERAC-linked event, fed by /api/events-merac
// (api/src/functions/eventsMerac.ts, gated on SG-Reports-Events).
//
// 2026-08-10 double-count fix: ticket income is a CHANNEL SPLIT — Online
// (advance web bookings) vs In-house (counter / walk-up) — summing to the
// correct total. Replaces the old Booked + Gate sum that double-counted
// advance-sold events (Tornado showed £111k vs the real £57k). Each card shows
// Online / In-house / Total (+ costs & net where costs exist). Click Online or
// In-house for that channel's per-ticket breakdown; click the card for both.
//
// A year selector (from dbo.Seasons) drives the whole tab; non-current seasons
// are live-proxied from the events feed. The All-Events card sums the selected
// season into one combined total.

interface RevLine { label: string; qty: number; pence: number }
interface CostLine { category: string; pence: number; lineCount: number }
interface EventCard {
  eventId: number
  name: string
  startDate: string
  endDate: string
  meracEventCode: string | null
  onlinePence: number
  onlineQty: number
  inhousePence: number
  inhouseQty: number
  totalRevenuePence: number
  totalCostsPence: number
  netPence: number
  onlineBreakdown: RevLine[]
  inhouseBreakdown: RevLine[]
  costLines: CostLine[]
}
interface Season { label: string; from: string; to: string; isCurrent: boolean }
interface Payload {
  asOf: string | null
  live?: boolean
  season: { label: string | null; from: string | null; to: string | null }
  seasons?: Season[]
  events: EventCard[]
}

// Shared "Refresh now" status (GET /api/events-merac/refresh) — polled by every
// open Events page, so a run anyone started, its outcome and the hourly lockout
// read the same for all viewers.
interface RefreshStatus {
  in_flight: boolean
  latest: {
    status: string
    requested_by: string
    requested_at: string | null
    started_at: string | null
    completed_at: string | null
    message: string | null
  } | null
  last_refresh: string | null
  lockout_minutes: number
  next_available_at: string | null
  locked: boolean
  can_request: boolean
}

function gbp(pence: number): string {
  const sign = pence < 0 ? '-' : ''
  return `${sign}£${Math.round(Math.abs(pence) / 100).toLocaleString('en-GB')}`
}

function fmtDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', timeZone: 'UTC' }
  try {
    const s = new Date(`${start}T00:00:00Z`)
    const e = new Date(`${end}T00:00:00Z`)
    const sStr = s.toLocaleDateString('en-GB', opts)
    const eStr = e.toLocaleDateString('en-GB', { ...opts, year: 'numeric' })
    return start === end ? eStr : `${sStr} – ${eStr}`
  } catch {
    return `${start} – ${end}`
  }
}

function relAsOf(iso: string | null): string {
  if (!iso) return 'never'
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/** "17 Sept 2026, 09:05" for the refresh status lines. */
function prettyDateTime(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** Turn a UPN ("gayl.ripley@nymr.co.uk") into a display name ("Gayl Ripley"). */
function prettyUser(upn: string | null | undefined): string {
  if (!upn) return 'someone'
  const local = upn.split('@')[0]
  return local.split(/[._-]+/).filter(Boolean).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ') || upn
}

export function EventsMeracDashboard({ me }: { me: { isEvents: boolean; isPower?: boolean } }) {
  const [data, setData] = useState<Payload | null>(null)
  const [seasons, setSeasons] = useState<Season[]>([])
  // The selected season window. null = the default current-season snapshot.
  const [window, setWindow] = useState<{ from: string; to: string } | null>(null)
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revModal, setRevModal] = useState<EventCard | null>(null)
  const [channelModal, setChannelModal] = useState<{ ev: EventCard; channel: 'online' | 'inhouse' } | null>(null)
  const [costModal, setCostModal] = useState<EventCard | null>(null)
  const [bookingsModal, setBookingsModal] = useState<EventCard | null>(null)
  const [unionModal, setUnionModal] = useState<{ name: string; events: EventCard[] } | null>(null)
  const [allModal, setAllModal] = useState<boolean>(false)

  // "Refresh now" (Admin / Power only): requests the Sync Hub events chain via
  // app.RefreshRequests and polls the SHARED status. When a run completes (the
  // snapshot's refreshed_at advances) every open page reloads its events.
  const [refreshStatus, setRefreshStatus] = useState<RefreshStatus | null>(null)
  const [posting, setPosting] = useState(false)
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null)
  const [reloadNonce, setReloadNonce] = useState(0)
  const lastRefreshRef = useRef<string | null>(null)
  const pollStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/events-merac/refresh', { credentials: 'include' })
      if (!res.ok) return
      const s = (await res.json()) as RefreshStatus
      setRefreshStatus(s)
      if (s.last_refresh && lastRefreshRef.current && s.last_refresh > lastRefreshRef.current) {
        setReloadNonce((n) => n + 1)
      }
      if (s.last_refresh) lastRefreshRef.current = s.last_refresh
    } catch {
      /* transient — retry next tick */
    }
  }, [])
  useEffect(() => {
    if (!me.isEvents) return
    void pollStatus()
    const t = globalThis.setInterval(() => void pollStatus(), 8000)
    return () => globalThis.clearInterval(t)
  }, [me.isEvents, pollStatus])
  const refreshBusy = (refreshStatus?.in_flight ?? false) || posting
  const refreshLocked = refreshStatus?.locked ?? false
  const onRefresh = useCallback(async () => {
    if (refreshBusy || refreshLocked) return
    setPosting(true)
    setRefreshMsg(null)
    try {
      const res = await fetch('/api/events-merac/refresh', { method: 'POST', credentials: 'include', headers: { Accept: 'application/json' } })
      if (!res.ok && res.status !== 202) {
        const b = await res.json().catch(() => ({} as { error?: string }))
        throw new Error(b.error || `Request failed (${res.status})`)
      }
      await pollStatus()
    } catch (err) {
      setRefreshMsg(err instanceof Error ? err.message : String(err))
    } finally {
      setPosting(false)
    }
  }, [refreshBusy, refreshLocked, pollStatus])

  // Group MERAC-linked cards by event code. A code shared by >1 event is a
  // split event (e.g. a visiting loco running Sept AND Oct under one MERAC
  // code) — those cards get a "combine" badge that opens a merged-total popup.
  const codeGroups = useMemo(() => {
    const m = new Map<string, EventCard[]>()
    for (const ev of data?.events ?? []) {
      if (!ev.meracEventCode) continue
      const arr = m.get(ev.meracEventCode) ?? []
      arr.push(ev)
      m.set(ev.meracEventCode, arr)
    }
    return m
  }, [data])

  // Season totals for the All-Events card.
  const totals = useMemo(() => {
    const evs = data?.events ?? []
    return {
      count: evs.length,
      online: evs.reduce((s, e) => s + e.onlinePence, 0),
      inhouse: evs.reduce((s, e) => s + e.inhousePence, 0),
      revenue: evs.reduce((s, e) => s + e.totalRevenuePence, 0),
      costs: evs.reduce((s, e) => s + e.totalCostsPence, 0),
      net: evs.reduce((s, e) => s + e.netPence, 0),
    }
  }, [data])

  useEffect(() => {
    if (!me.isEvents) { setLoading(false); return }
    let live = true
    setLoading(true)
    const qs = window ? `?from=${window.from}&to=${window.to}` : ''
    fetch(`/api/events-merac${qs}`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
        return res.json() as Promise<Payload>
      })
      .then((d) => {
        if (!live) return
        setData(d)
        setError(null)
        if (Array.isArray(d.seasons) && d.seasons.length) setSeasons(d.seasons)
        if (selectedLabel === null) setSelectedLabel(d.season?.label ?? null)
      })
      .catch((e) => { if (live) setError(String(e?.message ?? e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [me.isEvents, window, reloadNonce])   // eslint-disable-line react-hooks/exhaustive-deps

  function onPickSeason(label: string) {
    const s = seasons.find((x) => x.label === label)
    setSelectedLabel(label)
    if (!s || s.isCurrent) setWindow(null)          // current season = the fast snapshot path
    else setWindow({ from: s.from, to: s.to })      // other years = live proxy
  }

  if (!me.isEvents) {
    return (
      <main className="app-main">
        <div className="placeholder">
          <strong>Events</strong>
          <p>You don’t have access to the Events tab. Ask an administrator to add you to <code>SG-Reports-Events</code>.</p>
        </div>
      </main>
    )
  }

  return (
    <main className="app-main">
      <div className="em-head">
        <div>
          <p className="em-sub">
            Ticket revenue (Online + In-house), costs &amp; net per MERAC-linked event
            {data?.season?.label ? ` · ${data.season.label} season` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {seasons.length > 0 && (
            <label style={{ fontSize: '0.82rem', color: '#4a4a4a', display: 'flex', alignItems: 'center', gap: 6 }}>
              Season
              <select
                value={selectedLabel ?? ''}
                onChange={(e) => onPickSeason(e.target.value)}
                style={{ fontSize: '0.85rem', padding: '4px 8px', border: '1px solid #cfc9bd', borderRadius: 6, background: '#fff' }}
              >
                {selectedLabel === null && <option value="">Current</option>}
                {seasons.map((s) => (
                  <option key={s.label} value={s.label}>{s.label}{s.isCurrent ? ' (current)' : ''}</option>
                ))}
              </select>
            </label>
          )}
          {data && <div className="em-asof">{data.live ? 'Live' : 'Updated'} {relAsOf(data.asOf)}</div>}
        </div>
      </div>

      {me.isPower && (
        <div className="em-refresh">
          <div className="em-refresh-row">
            <span className="em-refresh-pill" title="Runs five Sync Hub jobs against the MERAC ticketing system — limited to once an hour">High-load job</span>
            <button
              type="button"
              className="em-refresh-btn"
              onClick={() => void onRefresh()}
              disabled={refreshBusy || refreshLocked}
              title={refreshLocked && refreshStatus?.next_available_at
                ? `Available again at ${prettyDateTime(refreshStatus.next_available_at)}`
                : 'Pull the latest event figures from the MERAC ticketing system — about 7 minutes; the tab updates for everyone when it completes'}
            >
              {refreshBusy ? '↻ Refreshing…' : '↻ Refresh now'}
            </button>
          </div>
          {refreshStatus?.in_flight && (
            <span className="em-refresh-msg">
              🔄 Refresh in progress — started by {prettyUser(refreshStatus.latest?.requested_by)}
              {refreshStatus.latest?.requested_at ? ` at ${prettyDateTime(refreshStatus.latest.requested_at)}` : ''}
              {refreshStatus.latest?.message && refreshStatus.latest.status === 'running' ? ` — ${refreshStatus.latest.message}` : ''}.
              {' '}This tab will update automatically for everyone when it finishes.
            </span>
          )}
          {refreshMsg && <span className="em-refresh-msg em-refresh-err">{refreshMsg}</span>}
          <span className="em-refresh-note">
            This is a <strong>high-load job</strong>: it pulls the events data from the MERAC ticketing system by running five Sync Hub jobs in sequence (about 7 minutes) and reloads this tab for everyone when it finishes.
            To prevent system overload from over-frequent running it can be requested <strong>once an hour</strong>.
            {' '}Data last refreshed at {prettyDateTime(refreshStatus?.last_refresh ?? data?.asOf ?? null)}.
            {refreshStatus?.latest && !refreshStatus.in_flight && (
              <>
                {' '}Last run requested by {prettyUser(refreshStatus.latest.requested_by)}
                {refreshStatus.latest.requested_at ? ` at ${prettyDateTime(refreshStatus.latest.requested_at)}` : ''}
                {refreshStatus.latest.status === 'completed'
                  ? ' — ✓ successful'
                  : refreshStatus.latest.status === 'error'
                  ? ` — ✗ failed${refreshStatus.latest.message ? ` (${refreshStatus.latest.message})` : ''}`
                  : ''}.
              </>
            )}
            {' '}
            {refreshStatus?.in_flight
              ? 'Available again one hour after the current run was requested.'
              : refreshLocked && refreshStatus?.next_available_at
              ? <strong>Available again at {prettyDateTime(refreshStatus.next_available_at)}.</strong>
              : refreshStatus
              ? <strong>Available to run now.</strong>
              : ''}
          </span>
        </div>
      )}

      {loading && <div className="em-state">Loading events…</div>}
      {error && <div className="em-state em-error">Couldn’t load events: {error}</div>}
      {data && !loading && !error && data.events.length === 0 && (
        <div className="em-state">No MERAC-linked events in this season.</div>
      )}

      {data && !loading && !error && data.events.length > 0 && (
        <>
          {/* All-Events summary card — one combined total for the whole season. */}
          <button
            type="button"
            className="em-card em-card--all"
            onClick={() => setAllModal(true)}
            title="See every event in this season"
            style={{ width: '100%', marginBottom: 16, textAlign: 'left' }}
          >
            <div className="em-card__head">
              <span className="em-card__name">All events{selectedLabel ? ` · ${selectedLabel}` : ''}</span>
              <span className="em-card__dates">{totals.count} event{totals.count === 1 ? '' : 's'}</span>
            </div>
            <dl className="em-rows" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '4px 18px' }}>
              <div className="em-row"><dt>Online</dt><dd>{gbp(totals.online)}</dd></div>
              <div className="em-row"><dt>In-house</dt><dd>{gbp(totals.inhouse)}</dd></div>
              <div className="em-row em-row--total"><dt>Total revenue</dt><dd>{gbp(totals.revenue)}</dd></div>
              {totals.costs > 0 && (
                <div className={`em-row em-row--net ${totals.net < 0 ? 'is-neg' : 'is-pos'}`}><dt>Net</dt><dd>{gbp(totals.net)}</dd></div>
              )}
            </dl>
            <div className="em-card__foot">breakdown by event ▸</div>
          </button>

          <div className="em-grid">
          {data.events.map((ev) => {
            const hasCosts = ev.totalCostsPence > 0 || ev.costLines.length > 0
            const group = ev.meracEventCode ? codeGroups.get(ev.meracEventCode) : undefined
            const isSplit = !!group && group.length > 1
            const unionName = ev.name.replace(/\s*\(.*\)\s*$/, "").trim()
            return (
              <button
                type="button"
                key={ev.eventId}
                className="em-card"
                onClick={() => setRevModal(ev)}
                title="Click for the full revenue breakdown"
              >
                <div className="em-card__head">
                  <span className="em-card__name">{ev.name}</span>
                  <span className="em-card__dates">{fmtDateRange(ev.startDate, ev.endDate)}</span>
                  {isSplit && (
                    <span
                      role="button"
                      tabIndex={0}
                      title={`Combined total of all ${group!.length} ${unionName} dates`}
                      aria-label={`Combined total of all ${group!.length} ${unionName} dates`}
                      onClick={(e) => { e.stopPropagation(); setUnionModal({ name: unionName, events: group! }) }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setUnionModal({ name: unionName, events: group! }) } }}
                      style={{
                        marginLeft: "auto", flex: "0 0 auto", cursor: "pointer",
                        width: 22, height: 22, borderRadius: "50%",
                        border: "1px solid #1f4d3f", color: "#1f4d3f",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        fontSize: 13, lineHeight: 1, fontWeight: 700,
                      }}
                    >⧉</span>
                  )}
                </div>

                <dl className="em-rows">
                  <div
                    className="em-row em-row--cost"
                    role="button"
                    tabIndex={0}
                    title="Advance web bookings — click for the per-ticket breakdown"
                    onClick={(e) => { e.stopPropagation(); setChannelModal({ ev, channel: 'online' }) }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setChannelModal({ ev, channel: 'online' }) } }}
                  >
                    <dt>Online <span className="em-link">view ▾</span></dt>
                    <dd>{gbp(ev.onlinePence)}</dd>
                  </div>
                  <div
                    className="em-row em-row--cost"
                    role="button"
                    tabIndex={0}
                    title="Counter / walk-up sold at the till — click for the per-ticket breakdown"
                    onClick={(e) => { e.stopPropagation(); setChannelModal({ ev, channel: 'inhouse' }) }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setChannelModal({ ev, channel: 'inhouse' }) } }}
                  >
                    <dt>In-house <span className="em-link">view ▾</span></dt>
                    <dd>{gbp(ev.inhousePence)}</dd>
                  </div>
                  <div className="em-row em-row--total">
                    <dt>Total revenue</dt>
                    <dd>{gbp(ev.totalRevenuePence)}</dd>
                  </div>

                  {hasCosts && (
                    <>
                      <div
                        className="em-row em-row--cost"
                        role="button"
                        tabIndex={0}
                        title="Click to see the cost lines"
                        onClick={(e) => { e.stopPropagation(); setCostModal(ev) }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setCostModal(ev) }
                        }}
                      >
                        <dt>Costs <span className="em-link">view ▾</span></dt>
                        <dd>−{gbp(ev.totalCostsPence)}</dd>
                      </div>
                      <div className={`em-row em-row--net ${ev.netPence < 0 ? 'is-neg' : 'is-pos'}`}>
                        <dt>Net (rev − costs)</dt>
                        <dd>{gbp(ev.netPence)}</dd>
                      </div>
                    </>
                  )}

                  {/* Bookings sits INSIDE the row list so it lines up with Online /
                      In-house / Total revenue (it used to hang outside the list's
                      padding, flush with the card edge — Chris, 2026-09-17). */}
                  {ev.meracEventCode && (
                    <div
                      className="em-row em-row--cost em-row--bookings"
                      role="button"
                      tabIndex={0}
                      title="View this event's bookings and email the attendees"
                      onClick={(e) => { e.stopPropagation(); setBookingsModal(ev) }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setBookingsModal(ev) }
                      }}
                    >
                      <dt>Bookings <span className="em-link">view ▾</span></dt>
                      <dd />
                    </div>
                  )}
                </dl>

                <div className="em-card__foot">
                  {ev.meracEventCode ? `MERAC ${ev.meracEventCode}` : 'MERAC-linked'} · breakdown ▸
                </div>
              </button>
            )
          })}
          </div>
        </>
      )}

      {revModal && (
        <EmModal title={`${revModal.name} — revenue breakdown`} onClose={() => setRevModal(null)}>
          <ChannelTable title="Online (advance web bookings)" lines={revModal.onlineBreakdown} totalPence={revModal.onlinePence} />
          <div style={{ height: 12 }} />
          <ChannelTable title="In-house (counter / walk-up)" lines={revModal.inhouseBreakdown} totalPence={revModal.inhousePence} />
          <table className="em-table" style={{ marginTop: 12 }}>
            <tfoot>
              <tr><td>Total revenue</td><td className="num" /><td className="num">{gbp(revModal.totalRevenuePence)}</td></tr>
            </tfoot>
          </table>
        </EmModal>
      )}

      {channelModal && (
        <EmModal
          title={`${channelModal.ev.name} — ${channelModal.channel === 'online' ? 'Online (advance web bookings)' : 'In-house (counter / walk-up)'}`}
          onClose={() => setChannelModal(null)}
        >
          <ChannelTable
            title={channelModal.channel === 'online' ? 'Online' : 'In-house'}
            lines={channelModal.channel === 'online' ? channelModal.ev.onlineBreakdown : channelModal.ev.inhouseBreakdown}
            totalPence={channelModal.channel === 'online' ? channelModal.ev.onlinePence : channelModal.ev.inhousePence}
          />
        </EmModal>
      )}

      {costModal && (
        <EmModal title={`${costModal.name} — costs`} onClose={() => setCostModal(null)}>
          {costModal.costLines.length === 0 ? (
            <p className="em-empty">No costs added.</p>
          ) : (
            <table className="em-table">
              <thead><tr><th>Category</th><th className="num">Lines</th><th className="num">Amount</th></tr></thead>
              <tbody>
                {costModal.costLines.map((c, i) => (
                  <tr key={i}>
                    <td>{c.category}</td>
                    <td className="num">{c.lineCount.toLocaleString('en-GB')}</td>
                    <td className="num">{gbp(c.pence)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td>Total costs</td><td className="num" /><td className="num">{gbp(costModal.totalCostsPence)}</td></tr>
              </tfoot>
            </table>
          )}
        </EmModal>
      )}
      {allModal && data && (
        <AllEventsModal label={selectedLabel} events={data.events} totals={totals} onClose={() => setAllModal(false)} />
      )}
      {unionModal && (
        <EventUnionModal name={unionModal.name} events={unionModal.events} onClose={() => setUnionModal(null)} />
      )}
      {bookingsModal && bookingsModal.meracEventCode && (
        <EventBookingsModal event={bookingsModal} onClose={() => setBookingsModal(null)} />
      )}
    </main>
  )
}

// A per-channel per-ticket-class breakdown table.
function ChannelTable({ title, lines, totalPence }: { title: string; lines: RevLine[]; totalPence: number }) {
  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 13, margin: '0 0 4px' }}>{title}</div>
      {lines.length === 0 ? (
        <p className="em-empty">No {title.toLowerCase()} recorded.</p>
      ) : (
        <table className="em-table">
          <thead><tr><th>Ticket</th><th className="num">Tickets</th><th className="num">Revenue</th></tr></thead>
          <tbody>
            {lines.map((r, i) => (
              <tr key={i}><td>{r.label}</td><td className="num">{r.qty.toLocaleString('en-GB')}</td><td className="num">{gbp(r.pence)}</td></tr>
            ))}
          </tbody>
          <tfoot><tr><td>Subtotal</td><td className="num" /><td className="num">{gbp(totalPence)}</td></tr></tfoot>
        </table>
      )}
    </div>
  )
}

// All-Events modal — every event in the loaded season, with a combined total.
function AllEventsModal({
  label, events, totals, onClose,
}: {
  label: string | null
  events: EventCard[]
  totals: { count: number; online: number; inhouse: number; revenue: number; costs: number; net: number }
  onClose: () => void
}) {
  const sorted = [...events].sort((a, b) => b.totalRevenuePence - a.totalRevenuePence)
  return (
    <EmModal title={`All events${label ? ` · ${label}` : ''} — ${totals.count} event${totals.count === 1 ? '' : 's'}`} onClose={onClose}>
      <table className="em-table">
        <thead><tr><th>Event</th><th className="num">Online</th><th className="num">In-house</th><th className="num">Total</th></tr></thead>
        <tbody>
          {sorted.map((e) => (
            <tr key={e.eventId}>
              <td>{e.name} <span style={{ color: '#888' }}>({fmtDateRange(e.startDate, e.endDate)})</span></td>
              <td className="num">{gbp(e.onlinePence)}</td>
              <td className="num">{gbp(e.inhousePence)}</td>
              <td className="num">{gbp(e.totalRevenuePence)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Combined</td>
            <td className="num">{gbp(totals.online)}</td>
            <td className="num">{gbp(totals.inhouse)}</td>
            <td className="num">{gbp(totals.revenue)}</td>
          </tr>
        </tfoot>
      </table>
    </EmModal>
  )
}

// ---------------------------------------------------------------------------
//  Bookings block — the event's bookings grouped service date -> service ->
//  ticket type -> name + email (cache.EventBookingContacts). Tick groups to
//  build a recipient list, then compose + send ONE email (BCC everyone) from
//  the shared mailbox via /api/events-merac/email. Confirm shows the count.
// ---------------------------------------------------------------------------

interface BookingContact {
  service_date: string | null; service_time: string | null; service: string | null
  ticket_type: string | null; booking: string | null; qty: number
  customer: string | null; email: string | null
}
interface BookingsPayload {
  code: string
  window: { start_date: string | null; end_date: string | null } | null
  scope_used: 'dedicated' | 'window'
  dedicated_count: number
  window_count: number
  rows: BookingContact[]
  refreshed_at: string | null
}

function fmtSvcDate(iso: string | null): string {
  if (!iso) return 'No service date'
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
}

function EventBookingsModal({ event, onClose }: { event: EventCard; onClose: () => void }) {
  const code = event.meracEventCode as string
  const [data, setData] = useState<BookingsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(() => new Set()) // group keys "date|ticket"
  const [composing, setComposing] = useState(false)
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [sendResult, setSendResult] = useState<string | null>(null)

  // Scope: 'auto' lets the server pick (dedicated event tickets when they
  // exist in volume, otherwise everyone travelling during the event dates).
  const [scope, setScope] = useState<'auto' | 'dedicated' | 'window'>('auto')

  useEffect(() => {
    let live = true
    setLoading(true)
    fetch(`/api/events-merac/bookings?code=${encodeURIComponent(code)}&scope=${scope}`)
      .then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `${r.status}`) } return r.json() as Promise<BookingsPayload> })
      .then((d) => { if (live) { setData(d); setError(null); setSelected(new Set()) } })
      .catch((e) => { if (live) setError(String(e?.message ?? e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [code, scope])

  // View toggle: group each date's bookings by ticket type, or collate them by
  // the SERVICE they travel on (time + session description).
  const [groupBy, setGroupBy] = useState<'ticket' | 'service'>('ticket')

  // date -> (ticket type | service) -> attendees
  const grouped = useMemo(() => {
    const byDate = new Map<string, Map<string, BookingContact[]>>()
    for (const r of data?.rows ?? []) {
      const dk = r.service_date ?? ''
      const gk = groupBy === 'ticket'
        ? (r.ticket_type ?? '(no ticket type)')
        : `${r.service_time ? `${r.service_time} — ` : ''}${r.service ?? 'No service allocated'}`
      let groups = byDate.get(dk)
      if (!groups) { groups = new Map(); byDate.set(dk, groups) }
      const arr = groups.get(gk) ?? []
      arr.push(r)
      groups.set(gk, arr)
    }
    // Within each date, keep service view in time order (keys start "HH:MM — ").
    if (groupBy === 'service') {
      for (const [dk, groups] of byDate) {
        byDate.set(dk, new Map([...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))))
      }
    }
    return byDate
  }, [data, groupBy])

  const groupKey = (dk: string, tk: string) => `${dk}|${tk}`
  const toggle = (key: string) => setSelected((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n })

  // The recipient list = unique emails across every selected group.
  const recipients = useMemo(() => {
    const out = new Set<string>()
    for (const [dk, tickets] of grouped) {
      for (const [tk, rows] of tickets) {
        if (!selected.has(groupKey(dk, tk))) continue
        for (const r of rows) if (r.email) out.add(r.email.toLowerCase())
      }
    }
    return [...out]
  }, [grouped, selected])

  async function send() {
    setSending(true)
    setSendResult(null)
    try {
      const res = await fetch('/api/events-merac/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventCode: code, subject, message, recipients }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error((body as { error?: string }).error || `Send failed (${res.status})`)
      const b = body as { sent: number; from: string }
      setSendResult(`Sent to ${b.sent} recipient${b.sent === 1 ? '' : 's'} from ${b.from}.`)
      setComposing(false)
      setSelected(new Set())
      setSubject('')
      setMessage('')
    } catch (e) {
      setSendResult(`Couldn't send: ${String((e as Error)?.message ?? e)}`)
    } finally {
      setSending(false)
    }
  }

  return (
    <EmModal title={`${event.name} — bookings`} onClose={onClose}>
      {loading && <p className="em-empty">Loading bookings…</p>}
      {error && <p className="em-empty">Couldn&apos;t load bookings: {error}</p>}
      {sendResult && <p style={{ fontSize: '0.85rem', fontWeight: 600, color: sendResult.startsWith('Sent') ? '#0b3d2e' : '#a33' }}>{sendResult}</p>}
      {data && !loading && !error && data.rows.length === 0 && (
        <p className="em-empty">
          No advance bookings found for this event{data.window?.start_date ? ` (${fmtSvcDate(data.window.start_date)} – ${fmtSvcDate(data.window.end_date)})` : ''}.
          If bookings are expected, the cache may not have refreshed yet (Refresh-DiningEvents.ps1 on ONTRACK).
        </p>
      )}

      {data && !composing && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 8px' }}>
          <span style={{ fontSize: '0.78rem', color: '#6b6b6b' }}>
            Showing{data.window?.start_date ? ` ${fmtSvcDate(data.window.start_date)} – ${fmtSvcDate(data.window.end_date)}` : ''}:
          </span>
          <button
            type="button"
            onClick={() => setScope('dedicated')}
            style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: 12, cursor: 'pointer', border: '1px solid #0b3d2e', background: data.scope_used === 'dedicated' ? '#0b3d2e' : '#fff', color: data.scope_used === 'dedicated' ? '#fff' : '#0b3d2e' }}
          >
            Event tickets only ({data.dedicated_count})
          </button>
          <button
            type="button"
            onClick={() => setScope('window')}
            style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: 12, cursor: 'pointer', border: '1px solid #0b3d2e', background: data.scope_used === 'window' ? '#0b3d2e' : '#fff', color: data.scope_used === 'window' ? '#fff' : '#0b3d2e' }}
          >
            All bookings during event ({data.window_count})
          </button>
          <span style={{ fontSize: '0.78rem', color: '#6b6b6b', marginLeft: 10 }}>Group by:</span>
          <button
            type="button"
            onClick={() => { setGroupBy('ticket'); setSelected(new Set()) }}
            style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: 12, cursor: 'pointer', border: '1px solid #0b3d2e', background: groupBy === 'ticket' ? '#0b3d2e' : '#fff', color: groupBy === 'ticket' ? '#fff' : '#0b3d2e' }}
          >
            Ticket type
          </button>
          <button
            type="button"
            onClick={() => { setGroupBy('service'); setSelected(new Set()) }}
            style={{ fontSize: '0.78rem', padding: '3px 10px', borderRadius: 12, cursor: 'pointer', border: '1px solid #0b3d2e', background: groupBy === 'service' ? '#0b3d2e' : '#fff', color: groupBy === 'service' ? '#fff' : '#0b3d2e' }}
          >
            Service
          </button>
        </div>
      )}

      {data && data.rows.length > 0 && !composing && (
        <>
          {data.scope_used === 'window' && (
            <p style={{ fontSize: '0.8rem', color: '#8a6d1a', background: '#fdf6e3', border: '1px solid #e8d9a0', borderRadius: 6, padding: '6px 10px', margin: '0 0 8px' }}>
              Showing <strong>every advance booking travelling during the event dates</strong>{data.dedicated_count === 0 ? ' — this event has no dedicated ticket type in MERAC' : ''}.
            </p>
          )}
          <p style={{ fontSize: '0.8rem', color: '#6b6b6b', margin: '0 0 8px' }}>
            Tick the groups you want to email, then compose. Attendees without an email address are listed but can&apos;t be emailed.
          </p>
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {[...grouped.entries()].map(([dk, groups]) => (
              <div key={dk} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', margin: '6px 0 4px' }}>{fmtSvcDate(dk || null)}</div>
                {[...groups.entries()].map(([gk, rows]) => {
                  const key = groupKey(dk, gk)
                  const withEmail = rows.filter((r) => r.email).length
                  const svc = groupBy === 'ticket' ? rows.find((r) => r.service)?.service : null
                  const time = groupBy === 'ticket' ? rows.find((r) => r.service_time)?.service_time : null
                  return (
                    <div key={key} style={{ margin: '2px 0 6px 10px' }}>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: '0.82rem', cursor: 'pointer' }}>
                        <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)} />
                        <span>
                          <strong>{gk}</strong>
                          {svc ? ` · ${svc}` : ''}{time ? ` · ${time}` : ''}
                          {` — ${rows.length} booking${rows.length === 1 ? '' : 's'} (${withEmail} with email)`}
                        </span>
                      </label>
                      <div style={{ marginLeft: 26, fontSize: '0.78rem', color: '#555' }}>
                        {rows.map((r, i) => (
                          <div key={i}>
                            {r.customer ?? '(unknown)'}{r.qty > 1 ? ` ×${r.qty}` : ''} — {r.email ?? <em style={{ color: '#a33' }}>no email</em>}
                            {groupBy === 'service' && r.ticket_type ? <span style={{ color: '#8a8a8a' }}> · {r.ticket_type}</span> : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 8 }}>
            <span style={{ fontSize: '0.82rem', color: '#6b6b6b' }}>{recipients.length} unique email{recipients.length === 1 ? '' : 's'} selected</span>
            <button
              type="button"
              className="em-modal__x"
              style={{ background: '#0b3d2e', color: '#fff', borderRadius: 6, padding: '6px 14px', fontSize: '0.85rem', width: 'auto', height: 'auto' }}
              disabled={recipients.length === 0}
              onClick={() => setComposing(true)}
            >
              Email these people ▸
            </button>
          </div>
        </>
      )}

      {data && composing && (
        <>
          <p style={{ fontSize: '0.82rem', color: '#6b6b6b', margin: '0 0 8px' }}>
            One email will be sent from <strong>info@nymr.co.uk</strong> with all {recipients.length} recipient{recipients.length === 1 ? '' : 's'} in BCC (addresses stay hidden from each other).
          </p>
          <input
            type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 6, marginBottom: 8 }}
          />
          <textarea
            value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Email text…" rows={8}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 6, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={() => setComposing(false)} style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>Back</button>
            <button
              type="button"
              disabled={sending || !subject.trim() || !message.trim()}
              onClick={() => { if (window.confirm(`Send this email to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'} from info@nymr.co.uk?`)) void send() }}
              style={{ background: '#0b3d2e', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 600, cursor: 'pointer' }}
            >
              {sending ? 'Sending…' : `Send to ${recipients.length}`}
            </button>
          </div>
        </>
      )}
    </EmModal>
  )
}

// Combined-total popup for a split event (one MERAC code, multiple date
// instances — e.g. Sir Nigel Gresley Sept + Oct). Sums each instance's
// online/in-house/total and merges their per-ticket breakdowns by label.
function EventUnionModal({
  name,
  events,
  onClose,
}: {
  name: string
  events: EventCard[]
  onClose: () => void
}) {
  const sum = (f: (e: EventCard) => number) => events.reduce((a, e) => a + f(e), 0)
  const online = sum((e) => e.onlinePence)
  const inhouse = sum((e) => e.inhousePence)
  const total = sum((e) => e.totalRevenuePence)
  const byLabel = new Map<string, { qty: number; pence: number }>()
  for (const e of events) {
    for (const r of [...e.onlineBreakdown, ...e.inhouseBreakdown]) {
      const a = byLabel.get(r.label) ?? { qty: 0, pence: 0 }
      a.qty += r.qty
      a.pence += r.pence
      byLabel.set(r.label, a)
    }
  }
  const merged = [...byLabel.entries()]
    .map(([label, v]) => ({ label, qty: v.qty, pence: v.pence }))
    .sort((a, b) => b.pence - a.pence)

  return (
    <EmModal title={`${name} — combined (${events.length} dates)`} onClose={onClose}>
      <table className="em-table">
        <tbody>
          {events.map((e) => (
            <tr key={e.eventId}>
              <td>{e.name} <span style={{ color: "#888" }}>({fmtDateRange(e.startDate, e.endDate)})</span></td>
              <td className="num" />
              <td className="num">{gbp(e.totalRevenuePence)}</td>
            </tr>
          ))}
          <tr><td>Online</td><td className="num" /><td className="num">{gbp(online)}</td></tr>
          <tr><td>In-house</td><td className="num" /><td className="num">{gbp(inhouse)}</td></tr>
        </tbody>
        <tfoot>
          <tr><td>Combined total</td><td className="num" /><td className="num">{gbp(total)}</td></tr>
        </tfoot>
      </table>
      <div style={{ marginTop: 14, marginBottom: 4, fontWeight: 700, fontSize: 13 }}>Combined revenue breakdown</div>
      {merged.length === 0 ? (
        <p className="em-empty">No revenue recorded yet.</p>
      ) : (
        <table className="em-table">
          <thead><tr><th>Ticket</th><th className="num">Tickets</th><th className="num">Revenue</th></tr></thead>
          <tbody>
            {merged.map((r, i) => (
              <tr key={i}><td>{r.label}</td><td className="num">{r.qty.toLocaleString("en-GB")}</td><td className="num">{gbp(r.pence)}</td></tr>
            ))}
          </tbody>
          <tfoot><tr><td>Total</td><td className="num" /><td className="num">{gbp(total)}</td></tr></tfoot>
        </table>
      )}
    </EmModal>
  )
}

function EmModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="em-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="em-modal" onClick={(e) => e.stopPropagation()}>
        <div className="em-modal__head">
          <h2>{title}</h2>
          <button type="button" className="em-modal__x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="em-modal__body">{children}</div>
      </div>
    </div>
  )
}
